import React from 'react';
import './ui.css';

export interface StatusStep {
  value: string;
  label: string;
}

interface StatusStepperProps {
  /** Ordered lifecycle. Position in this array IS the progress order. */
  steps: StatusStep[];
  /** The current status. A value not in `steps` renders every step upcoming. */
  current: string;
  /** Omit to render read-only. */
  onChange?: (value: string) => void;
  /**
   * Terminal statuses that are NOT part of the linear run — e.g. 'cancelled'.
   * When `current` is one of these the final step renders as done+rejected.
   */
  terminalRejected?: string[];
  /** Terminal status that should render the final step done+accepted. */
  terminalAccepted?: string;
  className?: string;
  ariaLabel?: string;
}

/**
 * The dot-and-connector status stepper. The markup and class language were
 * previously hand-written inline — once for the quote-detail breadcrumb tracker
 * and again for the logistics movement form — so a third consumer meant a third
 * copy to keep in sync. Extracted here instead.
 *
 * Styles are the component's own (`.status-stepper*` in ui.css). They used to be
 * the header's `.gbt-*` rules, which meant this primitive only looked right
 * while Header.css happened to be in the bundle.
 */
const StatusStepper: React.FC<StatusStepperProps> = ({
  steps,
  current,
  onChange,
  terminalRejected = [],
  terminalAccepted,
  className = '',
  ariaLabel = 'Status',
}) => {
  const isRejected = terminalRejected.includes(current);
  const isAccepted = !!terminalAccepted && current === terminalAccepted;
  // A terminal status sits at the end of the run, not inside it.
  const currentIndex = isRejected || isAccepted
    ? steps.length - 1
    : steps.findIndex(s => s.value === current);

  const stateFor = (i: number): string => {
    const last = i === steps.length - 1;
    if (last && isRejected) return ' done rejected';
    if (last && isAccepted) return ' done accepted';
    if (currentIndex < 0) return ' upcoming';
    if (i < currentIndex) return ' done';
    if (i === currentIndex) return ' active';
    return ' upcoming';
  };

  return (
    <div className={`status-stepper ${className}`.trim()} role="group" aria-label={ariaLabel}>
      {steps.map((step, i) => (
        <React.Fragment key={step.value}>
          {i > 0 && <span className={`status-stepper-connector${i <= currentIndex ? ' filled' : ''}`} />}
          {onChange ? (
            <button
              type="button"
              className={`status-stepper-step status-stepper-step-btn${stateFor(i)}`}
              onClick={() => onChange(step.value)}
              aria-current={i === currentIndex ? 'step' : undefined}
            >
              <span className="status-stepper-dot">{i + 1}</span> <span className="status-stepper-label">{step.label}</span>
            </button>
          ) : (
            <span className={`status-stepper-step${stateFor(i)}`} aria-current={i === currentIndex ? 'step' : undefined}>
              <span className="status-stepper-dot">{i + 1}</span> <span className="status-stepper-label">{step.label}</span>
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default StatusStepper;
